import React, { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import thinkLogo from "../../assets/think-logo-192.png";
import { fetchMilestones } from "../../store/dashboard/slice";
import { selectDecidedCapsuleId } from "../../store/selectors/experience";
import { fetchSquadUsers } from "../../store/tasks/slice";
import {
  fetchCapsuleUser,
  fetchCurrentLevel,
  selectActiveCohortId,
  selectActiveContext,
  selectActiveSquadId,
  selectSwitchableContexts,
} from "../../store/user/slice";
import Icons from "../common/Icons";
import { SuccessDialog } from "../common/Modals/SuccessDialog";
import { CancelWarning } from "../Profile/CancelWarning";
import TaskCreationForm from "../TaskCreation/TaskCreationForm";
import TaskModifyForm from "../TaskModify/TaskModifyForm";
import { describeContext, ExperienceSwitcherModal } from "./ExperienceChooser";
import SideNavIcon from "./SideNavIcon";

/* Two arrows passing each other: "switch". Inline, like the rail's glyphs. */
const SwitchGlyph = ({ color }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * EXPERIENCE SWITCHER: THE RAIL ENTRY.
 *
 * Only for a learner with two or more discovered contexts. It names the
 * active experience and opens the chooser in the design-system Modal. On a
 * phone and on the collapsed rail it is the glyph alone, so the accessible
 * name (and the hover title) carries the experience. The Modal wrapper keeps
 * focus inside and returns it here when it closes.
 */
const ExperienceSwitch = ({ isCollapsed }) => {
  const contexts = useSelector(selectSwitchableContexts);
  const activeContext = useSelector(selectActiveContext);
  const [isOpen, setIsOpen] = useState(false);
  if (contexts.length === 0) return null;
  const activeTitle = activeContext
    ? describeContext(
        activeContext,
        contexts.findIndex(
          (context) => context.squad_id === activeContext.squad_id,
        ) + 1 || null,
      ).title
    : null;
  const name = activeTitle
    ? `switch experience. now in ${activeTitle}`
    : "choose your experience";
  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={name}
        aria-haspopup="dialog"
        title={name}
        data-testid="experience-switch"
        className={`
          xe-focus menu-item-hover w-full flex items-center gap-3 py-2.5 px-3 rounded-md
          transition-all duration-200 ease-out text-left
        `}
        style={{ color: "#B4C1E4" }}
      >
        <span className="flex items-center justify-center w-[18px] h-[18px] shrink-0">
          <SwitchGlyph color="#B4C1E4" />
        </span>
        {!isCollapsed && (
          <span className="hidden sm:flex min-w-0 flex-col">
            <span className="text-xs text-text-lv4">experience</span>
            <span className="max-w-[148px] truncate text-base font-medium text-text-lv2">
              {activeTitle ?? "choose one"}
            </span>
          </span>
        )}
      </button>
      <ExperienceSwitcherModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

// Sidebar custom styles for cosmic aesthetic
//
// Dashboard polish v2 sidebar pass (2026-05-20, Amir "Path B"):
// - Active item color shifted from persimmon (#FF5A5F, legacy alert
//   semantic) to pear (#DAEC37, v4 "you are here" semantic - matches
//   the journey trail + active step + active tab brand language).
// - Subtle bg gradient tint dropped slightly; pear-tinted at 8% so
//   the accent bar carries the active signal without a heavy fill.
//
// F02 (student frontend elevation, brief E31 "the shell is quiet"): the rail
// no longer breathes (the 8s inset glow) and the logo no longer bobs. The
// gradient and the pear active bar stay. Hover and keyboard focus now look
// alike: one quiet wash, plus the shared charlotte ring (`xe-focus`) where the
// browser's default outline used to be. Labels are the 14px navigation face.
const sidebarStyles = `
  .sidebar-container {
    background: linear-gradient(180deg, #1a2b4b 0%, #152441 50%, #132039 100%);
  }

  .menu-item-active {
    background: linear-gradient(90deg, rgba(218, 236, 55, 0.10) 0%, transparent 90%);
    border-left: 2px solid #DAEC37;
    box-shadow: inset 2px 0 8px rgba(218, 236, 55, 0.08);
  }

  .menu-item-hover {
    border-left: 2px solid transparent;
  }
  .menu-item-hover:hover,
  .menu-item-hover:focus-visible {
    background: rgba(255, 255, 255, 0.05);
  }

  .menu-item-chrome {
    opacity: 0.78;
  }
  .menu-item-chrome:hover,
  .menu-item-chrome:focus-visible {
    opacity: 1;
  }
`;
const Sidebar = ({ onOpenTaskForm, children }) => {
  const dispatch = useDispatch();
  const { isCancel } = useSelector((state) => state.profile);
  const [movePage, setMovePage] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isWarningDialog, setIsWarningDialog] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const location = useLocation();
  const { user, isCompletedReadObjectiveStep } = useSelector(
    (state) => state.user,
  );
  const navigate = useNavigate();
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isTaskModifyOpen, setIsTaskModifyOpen] = useState(false);
  const [active, setActive] = useState(1);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [isCreateTaskEnabled, setIsCreateTaskEnabled] = useState(false);
  const isUserJoined = !!user && !!user.cohortUser;

  // M2: the roster and milestones are the active delivery context's. Nothing
  // is read until discovery settles on one context.
  const activeSquadId = useSelector(selectActiveSquadId);
  const activeCohortId = useSelector(selectActiveCohortId);
  useEffect(() => {
    const fetchData = async () => {
      if (activeSquadId !== null && activeCohortId !== null) {
        await dispatch(fetchSquadUsers(activeSquadId));
        await dispatch(fetchMilestones(activeCohortId));
      }
    };
    fetchData();
  }, [dispatch, activeSquadId, activeCohortId]);

  // The store's one answer to which level the learner is on, which also
  // places a learner at a level boundary, where the mission names neither a
  // lesson nor a module.
  const capsuleId = useSelector(selectDecidedCapsuleId);

  useEffect(() => {
    const fetchLevel = async () => {
      if (capsuleId) {
        try {
          const level = await dispatch(fetchCurrentLevel(capsuleId)).unwrap();
          const readObjectiveStep = await dispatch(
            fetchCapsuleUser(user?.id),
          ).unwrap();
          setCurrentLevel(level);
          const isCompletedReadObjectiveStep = readObjectiveStep.filter(
            (step) => step.capsule_id === capsuleId,
          );
          if (level !== 1 || isCompletedReadObjectiveStep?.read_objective) {
            setIsCreateTaskEnabled(true);
          }
        } catch (error) {
          console.error("Error fetching current level:", error);
        }
      }
    };
    fetchLevel();
  }, [dispatch, capsuleId, user?.id]);

  useEffect(() => {
    setIsCreateTaskEnabled(isCompletedReadObjectiveStep);
  }, [isCompletedReadObjectiveStep]);

  // Items now carry a `glyph` key that maps to the bespoke
  // SideNavIcon glyph set. The legacy `icon` key (which mapped to the
  // platform's generic Icons component: home / direction / bookmarks
  // / podium / performance / user) is retired - those icons read as
  // utility chrome same way the journey step nodes did before the
  // StepGlyph rewrite. Same brand-cleanup pass.
  //
  // Journey item stays for v4 cohorts. The Journey page is the
  // multi-level retrospective view where students browse content
  // they have already completed - distinct from the dashboard
  // breadcrumb trail which shows only current-level progress.
  // (Earlier "hide for v4" filter was incorrect; the F4.2 redirect
  // it pointed at has been removed.)
  const mainMenuItems = useMemo(
    () => [
      { id: 1, title: "dashboard", path: "/dashboard", glyph: "dashboard" },
      { id: 2, title: "journey", path: "/journey", glyph: "journey" },
      // Tasks temporarily hidden - moved to Squad Workspace. Uncomment to restore:
      // { id: 3, title: "tasks", path: "/tasks", glyph: "tasks" },
      { id: 4, title: "bookmarks", path: "/bookmark", glyph: "bookmarks" },
      {
        id: 5,
        title: "leaderboard",
        path: "/leaderboard",
        glyph: "leaderboard",
      },
      { id: 6, title: "3cq", path: "/performance", glyph: "3cq" },
    ],
    [],
  );

  const bottomMenuItems = useMemo(
    () => [
      { id: 7, title: "profile", path: "/profile", glyph: "profile" },
      // Tutorials temporarily hidden. Uncomment to restore:
      // { id: 8, title: "tutorials", path: "/tutorials", glyph: "tutorials" },
      // Feedback temporarily hidden. Will build in-app form for future cohort:
      // { id: 9, title: "feedback", path: "/feedback", glyph: "feedback" },
    ],
    [],
  );

  useEffect(() => {
    const items = [...mainMenuItems, ...bottomMenuItems];
    const activeItem = items.find((item) => item.path === location.pathname);
    setActive(activeItem?.id);
  }, [location.pathname, bottomMenuItems, mainMenuItems]);

  const MenuItem = ({ item, isChrome = false }) => {
    const isActive = active === item.id;
    return (
      <button
        onClick={() => {
          setMovePage(item.id);
          if (item.title === "Create Task") {
            onOpenTaskForm();
          } else {
            location.pathname === "/profile" && isCancel && item.id !== 7
              ? setIsWarningDialog(true)
              : navigate(item.path);
          }
        }}
        aria-label={item.title}
        aria-current={isActive ? "page" : undefined}
        className={`
          xe-focus w-full flex items-center gap-3 py-2.5 px-3 rounded-md
          transition-all duration-200 ease-out group
          ${isActive ? "menu-item-active" : "menu-item-hover"}
          ${isChrome && !isActive ? "menu-item-chrome" : ""}
        `}
        style={{
          color: isActive ? "#DAEC37" : "#B4C1E4",
        }}
      >
        <div
          className={`
            flex items-center justify-center w-[18px] h-[18px]
            transition-transform duration-200
            ${isActive ? "scale-105" : "group-hover:scale-105"}
          `}
        >
          <SideNavIcon
            type={item.glyph}
            color={isActive ? "#DAEC37" : "#B4C1E4"}
          />
        </div>
        {!isCollapsed && (
          <span
            className="hidden sm:inline text-base font-medium transition-colors duration-200"
            style={{
              color: isActive ? "#DAEC37" : "#B4C1E4",
            }}
          >
            {item.title}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <style>{sidebarStyles}</style>
      <div className="flex h-dvh overflow-y-auto scrollbar-hidden">
        <div
          className={`
            sidebar-container transition-all duration-300 w-auto
            border-r border-white/5
          `}
        >
          <div className="flex flex-col py-6 px-2 h-full">
            {/* Think Logo (still: F02) */}
            <div className="flex justify-center mb-8 px-2">
              <div
                className={`
                  flex items-center justify-center
                  transition-all duration-300
                  ${isCollapsed ? "w-8 h-8" : "w-9 h-9"}
                `}
              >
                <img
                  src={thinkLogo}
                  alt="Think"
                  className="w-full h-full object-contain"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(171, 235, 247, 0.25))",
                  }}
                />
              </div>
            </div>

            {/* Main Menu Items.
                Density bumped from space-y-1 (4px) -> space-y-1.5 (6px)
                so the sidebar reads as scannable navigation rather
                than a tight list of buttons. */}
            <div className="flex-1 px-1 space-y-1.5">
              <ExperienceSwitch isCollapsed={isCollapsed} />
              {isUserJoined ? (
                <>
                  {mainMenuItems.map((item) => (
                    <MenuItem key={item.id} item={item} />
                  ))}
                </>
              ) : (
                <button
                  onClick={() => navigate("/season")}
                  aria-label="dashboard"
                  className={`
                    ${!isCollapsed ? "px-3" : "justify-center items-center w-full"}
                    xe-focus w-full py-2.5 flex items-center gap-x-3 rounded-md cursor-pointer
                    menu-item-active
                  `}
                  style={{ color: "#DAEC37" }}
                >
                  <SideNavIcon type="dashboard" color="#DAEC37" />
                  {!isCollapsed && (
                    <span className="hidden sm:inline text-base font-medium">
                      dashboard
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Subtle divider between primary nav and chrome group.
                Chrome group (profile + collapse) is visually
                subordinated below at 78% opacity baseline. */}
            <div className="mx-3 my-3">
              <div
                className="h-px w-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(171, 235, 247, 0.15) 50%, transparent 100%)",
                }}
              />
            </div>

            {/* Bottom Menu Items (chrome group: profile + future
                tutorials/feedback). Visually subordinated via the
                menu-item-chrome opacity treatment so they don't
                compete with primary nav for attention. */}
            <div className="px-1 space-y-1.5">
              {bottomMenuItems.map((item) => (
                <MenuItem key={item.id} item={item} isChrome />
              ))}
            </div>

            {/* Collapse Button: lowercase typography lock (was
                UPPERCASE), chrome-opacity baseline matches the bottom
                menu items above. */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? "expand the sidebar" : undefined}
              className={`
                xe-focus hidden sm:flex items-center gap-2 px-3 py-2 mx-1 mt-2 rounded-md
                text-text-lv4 transition-all duration-200
                hover:bg-white/5 hover:text-text-lv2
                menu-item-chrome
                group
              `}
            >
              <div className="transition-transform duration-200 group-hover:scale-105">
                <Icons
                  type={isCollapsed ? "show" : "hide"}
                  className="w-4 h-4"
                  color="#B4C1E4"
                />
              </div>
              {!isCollapsed && (
                <span className="text-sm font-medium">collapse</span>
              )}
            </button>
          </div>
        </div>
        {children}
      </div>

      {/* Other Dialogs */}
      <CancelWarning
        isOpen={isWarningDialog}
        active={movePage}
        navigate={navigate}
        items={mainMenuItems.concat(bottomMenuItems)}
        setActive={setActive}
        onClose={() => setIsWarningDialog(false)}
      />
      <TaskCreationForm
        isOpen={isTaskModalOpen}
        setIsSuccessDialogOpen={setIsSuccessDialogOpen}
        setSelectedTaskDetails={setSelectedTaskDetails}
        onClose={() => setIsTaskModalOpen(false)}
      />
      {isTaskModifyOpen && (
        <TaskModifyForm
          isOpen={isTaskModifyOpen}
          onClose={() => setIsTaskModifyOpen(false)}
          task={selectedTaskDetails}
        />
      )}
      <SuccessDialog
        isOpen={isSuccessDialogOpen}
        setIsPreviousModalOpen={setIsTaskModifyOpen}
        onClose={() => {
          setIsSuccessDialogOpen(false);
        }}
        xp={15}
        title="task created"
        label="breaking it down is half the battle."
      />
    </>
  );
};

export default Sidebar;
